import type { SpanRecord } from '@apm/contracts';
import { useMemo } from 'react';
import { SPAN_KIND_LABEL, TIP } from '../terms';
import { formatDuration } from '../time';
import { Chip, EmptyState } from '../ui';
import { layoutSpans, SPAN_KIND_BAR, SPAN_KIND_TONE } from './timeline';

/**
 * 구간별 소요시간 막대 (prompts.md 7회차, 결정 3).
 *
 * 요청이 시작한 순간을 0으로 놓은 시간축 위에 구간을 시작 순으로 한 줄씩 놓는다. 오류는 빨강.
 * X-View와 달리 그림판을 쓰지 않는다. 한 요청의 구간은 많아야 수백 개다.
 */

/** 오류 막대. 목록·상세의 오류 색과 같다 */
const ERROR_BAR = 'bg-error';

export function SpanBars({
  spans,
  fromMemory,
}: {
  spans: readonly SpanRecord[];
  /** 저장 파일이 아니라 서버 메모리에서 답한 경우 (콘솔 검토 C-18) */
  fromMemory?: boolean;
}) {
  // 정렬이 들어 있다. 같은 구간 목록이면 다시 배치하지 않는다 (D-C2)
  const timeline = useMemo(() => layoutSpans(spans), [spans]);
  if (timeline.bars.length === 0) {
    return (
      <EmptyState
        title="구간 정보가 아직 저장되지 않았다"
        hint={
          fromMemory
            ? `${TIP.fromMemory} 구간은 저장이 끝난 뒤에 보인다`
            : '몇 초 뒤 다시 열어 볼 것'
        }
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="tabular flex items-center justify-between text-xs text-muted">
        <span>0ms</span>
        <span>전체 {formatDuration(timeline.totalMs)}</span>
      </div>
      <ol className="space-y-1">
        {timeline.bars.map((bar) => {
          const span = bar.span;
          const failed = span.status === 'error';
          return (
            <li
              key={span.spanId}
              className="grid grid-cols-[6rem_minmax(0,14rem)_minmax(0,1fr)_5rem] items-center gap-3"
            >
              <Chip tone={failed ? 'error' : SPAN_KIND_TONE[span.kind]}>
                {SPAN_KIND_LABEL[span.kind]}
              </Chip>
              {/* 이름은 막대 위에 겹치지 않게 따로 둔다. 겹치면 막대 색에 묻혀 읽을 수 없다 */}
              <span
                className={`truncate font-mono text-xs ${failed ? 'text-error' : 'text-muted'}`}
                title={span.name}
              >
                {span.name}
              </span>
              <span
                className="relative block h-5 rounded-md bg-band"
                title={`${span.name} · ${formatDuration(span.durationMs)} · 시작 +${formatDuration(bar.offsetMs)}`}
              >
                <i
                  aria-hidden="true"
                  className={`absolute inset-y-0 rounded-md ${failed ? ERROR_BAR : SPAN_KIND_BAR[span.kind]}`}
                  style={{ left: `${bar.leftPct}%`, width: `${bar.widthPct}%` }}
                />
              </span>
              <span
                className={`tabular text-right font-mono text-xs ${failed ? 'text-error' : 'text-ink'}`}
              >
                {formatDuration(span.durationMs)}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
